import React from "react";
import {
  Activity, Bot, ShieldCheck, AlertTriangle, HelpCircle,
  TrendingUp, Users, Clock3, Coins, Scale,
} from "lucide-react";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";

/**
 * VolumeAuthenticityCard — surfaces the wash-trade detection block from
 * /api/scan (F3 PR-A). The backend already produces a friendly per-sub-score
 * `finding` string; the UI's job is to display those clearly and let you
 * eyeball the headline score in one glance.
 *
 * Props:
 *   volumeAuthenticity: report.volume_authenticity from /api/scan, shape:
 *     {
 *       ok, confidence: "high"|"low"|"unavailable",
 *       score: number|null, band, emoji, skipped_reason,
 *       sub_scores: [{name, score, weight, finding, metric, skipped}],
 *       sample: { swaps_inspected, unique_signers, total_sol },
 *       weights, generated_at,
 *     }
 */

// Cosmetic metadata per sub-score name. Keep keys exactly matching the
// backend WEIGHTS dict.
const SUB_META = {
  buy_sell_symmetry:        { label: "Buy/sell symmetry",       icon: Scale,      hint: "Wash bots round-trip in ~50/50 ratios." },
  tx_size_dispersion:       { label: "Tx-size dispersion",      icon: Coins,      hint: "Identical SOL amounts on every swap = bot." },
  wallet_concentration:     { label: "Wallet concentration",    icon: Users,      hint: "Most flow from a few wallets = wash farm." },
  time_clustering:          { label: "Time clustering",         icon: Clock3,     hint: "Metronome cadence between swaps = bot." },
  volume_liquidity_ratio:   { label: "Volume / liquidity",      icon: TrendingUp, hint: "24h volume vs pool depth — high turnover on a small pool is rarely organic." },
};

function bandMeta(band) {
  switch (band) {
    case "organic":
      return { label: "Organic", variant: "success", icon: ShieldCheck };
    case "suspicious":
      return { label: "Suspicious", variant: "warning", icon: AlertTriangle };
    case "likely_wash":
      return { label: "Likely wash", variant: "danger", icon: Bot };
    default:
      return { label: "Unknown", variant: "muted", icon: HelpCircle };
  }
}

function ScoreBar({ score, skipped }) {
  if (skipped) {
    return (
      <div className="h-1.5 rounded-full bg-[hsl(var(--bg-elev-3))] overflow-hidden">
        <div className="h-full w-full bg-[hsl(var(--border))] opacity-40" />
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const colorClass =
    pct >= 70 ? "bg-[hsl(var(--danger))]"
      : pct >= 31 ? "bg-[hsl(var(--warning))]"
      : "bg-[hsl(var(--success))]";
  return (
    <div className="h-1.5 rounded-full bg-[hsl(var(--bg-elev-3))] overflow-hidden">
      <div
        className={`h-full ${colorClass} transition-all`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function SubScoreRow({ sub }) {
  const meta = SUB_META[sub.name] || { label: sub.name, icon: HelpCircle, hint: "" };
  const Icon = meta.icon;
  const scoreLabel = sub.skipped ? "n/a" : `${Math.round(sub.score)}`;
  return (
    <div
      className="px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))]"
      data-testid={`volauth-sub-${sub.name}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={13} className="text-[hsl(var(--muted))] shrink-0" />
          <span className="text-xs font-medium truncate">{meta.label}</span>
          <span className="text-[10px] text-[hsl(var(--muted))]">
            · w {(sub.weight * 100).toFixed(0)}%
          </span>
        </div>
        <span
          className={`font-mono text-[11px] tabular-nums ${
            sub.skipped
              ? "text-[hsl(var(--muted))]"
              : sub.score >= 70
                ? "text-[hsl(var(--danger))]"
                : sub.score >= 31
                  ? "text-[hsl(var(--warning))]"
                  : "text-[hsl(var(--success))]"
          }`}
        >
          {scoreLabel}
        </span>
      </div>
      <div className="mt-1.5">
        <ScoreBar score={sub.score} skipped={sub.skipped} />
      </div>
      <p className="text-[11px] text-[hsl(var(--muted))] mt-1.5 leading-snug">
        {sub.finding}
      </p>
    </div>
  );
}


export function VolumeAuthenticityCard({ volumeAuthenticity }) {
  if (!volumeAuthenticity) {
    return null;
  }
  const v = volumeAuthenticity;
  const meta = bandMeta(v.band);
  const Icon = meta.icon;
  const showHeadline = v.ok && v.score != null;
  const inspected = v?.sample?.swaps_inspected ?? 0;
  const signers = v?.sample?.unique_signers ?? 0;

  // Stable sort sub-scores by weight desc so heaviest signals lead.
  const subs = Array.isArray(v.sub_scores)
    ? [...v.sub_scores].sort((a, b) => (b.weight || 0) - (a.weight || 0))
    : [];

  return (
    <Card className="p-0" data-testid="volume-authenticity-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Activity size={16} className="mt-0.5 text-[hsl(var(--muted))]" />
            <div>
              <CardTitle>Volume authenticity</CardTitle>
              <CardDescription>
                5-factor wash-trade score using the last {inspected || "—"} on-chain swaps and
                DexScreener turnover.
              </CardDescription>
            </div>
          </div>
          <Badge variant={meta.variant} className="gap-1">
            <Icon size={12} /> {meta.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Headline */}
        <div className="flex items-center justify-between gap-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums">
                {showHeadline ? Math.round(v.score) : "—"}
              </span>
              <span className="text-xs text-[hsl(var(--muted))]">/ 100</span>
              {v.emoji ? (
                <span className="ml-1 text-lg" aria-hidden="true">{v.emoji}</span>
              ) : null}
            </div>
            <div className="text-[11px] text-[hsl(var(--muted))] mt-0.5">
              {v.confidence === "high"
                ? `High confidence · ${inspected} swaps · ${signers} unique signers`
                : v.confidence === "low"
                  ? `Low confidence · only ${inspected} swaps available (need 30+)`
                  : `Score unavailable · ${v.skipped_reason || "no data"}`}
            </div>
          </div>
          {v.confidence === "low" ? (
            <Badge variant="warning" className="gap-1 shrink-0">
              <AlertTriangle size={11} /> data-sparse
            </Badge>
          ) : null}
        </div>

        {/* Sub-score grid */}
        {subs.length > 0 ? (
          <>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {subs.map((s) => (
                <SubScoreRow key={s.name} sub={s} />
              ))}
            </div>
          </>
        ) : null}

        <p className="text-[10px] text-[hsl(var(--muted))]">
          Score weighted 20/25/25/10/20 across the five factors. Higher = more wash-like.
          Bands: 0–30 organic · 31–69 suspicious · 70–100 likely wash.
          Per-swap factors require ≥30 Helius-fetched swaps; otherwise only the volume/liquidity ratio runs.
        </p>
      </CardContent>
    </Card>
  );
}

export default VolumeAuthenticityCard;
